import map from "lodash/map"
import isNull from "lodash/isNull"
import isEmpty from "lodash/isEmpty"
import every from "lodash/every"
import forEach from "lodash/forEach"
import React, { useState, useEffect, createRef } from "react"
import Typography from "component-lib/Base/components/Typography"
import { makeStyles } from "tss-react/mui"
import Box from "component-lib/Base/components/Box"
import {
  FormattedTextField,
  Button,
  ButtonArrow,
  ButtonPrimary,
} from "component-lib/Base"
import getContentUrl from "utils-lib/getContentUrl"
import { ReactSVG } from "react-svg"
import Slider from "react-slick"
import { BOOTSTRAP_GRID_IPAD_PRO_MAX } from "constants-lib/dimensions"
import { getWindow } from "utils-lib/getWindow"
import { getZoomLevel } from "utils-lib/getZoomLevel/getZoomLevel"
import Video from "./components/Video"

const ssrWindow = getWindow()

let sliderRef

const useStyles = makeStyles()(
  (
    theme,
    { backgroundColor, isExpanded, dynamicHeightMapping, marginLeft },
  ) => {
    const classStyles = {}

    // handle setting values for root class
    let bgColor = ``
    if (backgroundColor === `green1`) {
      bgColor = theme.palette.background.darkGreen
    }
    if (backgroundColor === `sand`) {
      bgColor = theme.palette.background.sand
    }
    if (backgroundColor === `beige`) {
      bgColor = theme.palette.background.beige
    }
    if (backgroundColor === `white`) {
      bgColor = theme.palette.background.white
    }

    classStyles.root = {
      backgroundColor: bgColor,
    }

    return {
      "body::-webkit-scrollbar": {
        display: "none",
      },
      root: {
        [theme.breakpoints.down(`md`)]: {
          ".slick-list": {
            display: `flex !important`,
            gap: "5px !important",
            marginLeft: "-2px !important",
          },
        },
        "& img": {
          objectFit: `cover`,
          objectPosition: `0 0`,
        },
        backgroundColor: classStyles.root.backgroundColor,
        [theme.breakpoints.down(`md`)]: {
          paddingBottom: `40px`,
          minHeight: `648px !important`,
          "& .slick-slider .next": {
            right: `3rem !important`,
            display: `block !important`,
          },
          "& .slick-slider .prev": {
            left: `3rem !important`,
            display: `block !important`,
          },
        },
        padding: `40px 0`,
        minHeight: `436px`,
        "& .next": {
          marginLeft: "50px",
        },
        "&  .prev": {
          marginRight: "50px",
        },
        "& .slick-prev:before": {
          display: "none !important",
        },
        "& .slick-next:before": {
          display: "none !important",
        },
        "& .slick-prev": {
          "&:focus": {
            outline: `2px auto -webkit-focus-ring-color !important`,
          },
        },
        "& .slick-next": {
          "&:focus": {
            outline: `2px auto -webkit-focus-ring-color !important`,
          },
        },
        [theme.breakpoints.down(`xl`)]: {
          minHeight: `678px !important`,
          "& button.slick-arrow": {
            bottom: `-32px !important`,
            position: `absolute !important`,
            top: `unset !important`,
          },
          "& .next": {
            right: `1rem`,
            position: `absolute`,
          },
          "& .prev": {
            left: `1rem`,
            position: `absolute`,
          },
        },
        [theme.breakpoints.down(`sm`)]: {
          "& button.slick-arrow": {
            bottom: `-8px !important`,
            position: `absolute !important`,
            top: `unset !important`,
          },
          "& .slick-slider .next": {
            display: `block !important`,
          },
          "& .slick-slider .prev": {
            display: `block !important`,
          },
        },
        "& a": {
          fontFamily: "maax-font-weight-bold",
          color: theme.palette.primary.main2_0,
          "&:hover": {
            color: theme.palette.secondary.main,
          },
        },
        "& .slick-slide img": {
          objectFit: `cover !important`,
          objectPosition: `0% 0% !important`,
          [theme.breakpoints.down(`3`)]: {
            flex: `1 !important`,
          },
        },
        "& .slick-list": {
          padding: `0 !important`,
        },
        "& .slick-dots": {
          position: "relative !important",
          bottom: `0 !important`,
          [theme.breakpoints.down(`sm`)]: {
            bottom: `0rem !important`,
            position: `relative !important`,
            width: `55% !important`,
            paddingTop: `12px!important`,
            margin: `0 23% !important`,
          },
          [theme.breakpoints.down(`xl`)]: {
            display: `flex !important`,
            justifyContent: `center !important`,
            width: `50% !important`,
            position: "relative !important",
            bottom: "-1.7rem !important",
            margin: "0 auto !important",
          },
        },
        "& .slick-dots li": {
          zIndex: `3 !important`,
        },
        "& .slick-dots button:focus": {
          outline: "2px auto -webkit-focus-ring-color",
        },
        "& .slick-dots  li button:before": {
          color: `${
            backgroundColor === `green1`
              ? theme.palette.common.white
              : theme.palette.common.black
          } !important`,
          fontSize: `12px !important`,
          border: `transparent !important`,
          marginTop: `0.125rem !important`,
          width: `0.7rem !important`,
          height: `0.7rem !important`,
          lineHeight: `0.7rem !important`,
          borderRadius: `50% !important`,
          paddingTop: `1.4px !important`,
          opacity: `1 !important`,
        },
        "& .slick-dots li.slick-active button:before": {
          border: `${
            backgroundColor === `green1`
              ? `2px solid ${theme.palette.common.white}`
              : `2px solid ${theme.palette.background.darkGreen}`
          } !important`,
          opacity: `1 !important`,
          color: `transparent !important`,
          fontSize: `12px !important`,
          lineHeight: `12px !important`,
          display: `block !important`,
        },
      },
      setHeight: {
        [theme.breakpoints.down(`sm`)]: {
          height: `100%`,
        },
      },
      eyebrowText: {
        color: theme.palette.primary.main2_0,
        textTransform: `uppercase`,
        fontSize: `14px`,
        lineHeight: `14px`,
      },
      playpause: {
        alignSelf: `flex-end`,
        [theme.breakpoints.down(`sm`)]: {
          padding: `0 16px 5px 16px !important`,
        },
      },
      carousel: {
        display: isExpanded ? `` : `none`,
        position: `relative`,
      },
      bodyText: {
        "& p": {
          marginBottom: `0 `,
        },
        "& *": {
          fontSize: `1rem !important`,
          [theme.breakpoints.up(`sm`)]: {
            fontSize: `inherit !important`,
          },
        },
        "& h1, h2, h3, h4, h5, h6": {
          fontSize: `1.5rem !important`,
          [theme.breakpoints.up(`sm`)]: {
            fontSize: `inherit !important`,
          },
        },
      },
      link: {
        paddingTop: `0 !important`,
        paddingBottom: `0 !important`,
        color: `${theme.palette.primary.main}`,
      },
      StoryCarouselContent: {
        minHeight: `436px`,
        [theme.breakpoints.down(`xl`)]: {
          paddingTop: `0`,
          minHeight: `578px`,
          margin: `0 4px`,
        },
        [theme.breakpoints.down(`lg`)]: {
          overflow: `hidden`,
        },
        [theme.breakpoints.down(`sm`)]: {
          minHeight: `440px`,
          margin: `0 4px`,
          flexDirection: `column !important`,
        },
        "& .video-thumbnail ": {
          left: `-290px !important`,
        },
      },
      imageVideo: {
        zIndex: `2`,
        width: `475px`,
        height: `326px`,
        "& .Video": {
          width: `475px !important`,
          height: `326px !important`,
          [theme.breakpoints.down(`xl`)]: {
            width: `100% !important`,
          },
          "& .btn": {
            height: `267px`,
          },
        },
        [theme.breakpoints.down(`xl`)]: {
          width: `345px !important`,
          height: `232px !important`,
          left: `29%`,
          position: `relative`,
          top: `-10rem`,
          "& .picture": {
            width: `345px !important`,
            height: `200px !important`,
          },
          "& .Video": {
            width: `345px`,
            height: `232px !important`,
            "& .btn": {
              height: `194px`,
            },
          },
        },
        [theme.breakpoints.down(`sm`)]: {
          paddingTop: `0`,
          height: `100% !important`,
          left: 1,
          top: 0,
          width: `100% !important`,
          "& .picture": {
            width: "100% !important",
          },
        },
      },
      imageVideoZoom: {
        [theme.breakpoints.down(`lg`)]: {
          left: `15%`,
          top: `-13rem`,
        },
        [theme.breakpoints.down(`sm`)]: {
          left: `0`,
          top: `0`,
        },
      },
      textArea: {
        boxShadow: "2px 1px rgb(0 0 0 / 10%), -0.2em 1px 1em rgb(0 0 0 / 20%)",
        borderRadius: "4px",
        position: `relative`,
        width: `100%`,
        maxWidth: `760px`,
        marginLeft: `-6.125rem`,
        zIndex: `1`,
        [theme.breakpoints.down(`sm`)]: {
          width: `100%`,
          boxShadow: `1px 4px 8px 2px rgb(0 0 0 / 10%), 0 0 1px rgb(0 0 0 / 20%)`,
          minHeight: dynamicHeightMapping,
          height: dynamicHeightMapping,
          margin: `0 0 1rem 0 !important`,
          paddingBottom: `2rem`,
        },
        [theme.breakpoints.down(`xl`)]: {
          boxShadow: `1px 4px 8px 2px rgb(0 0 0 / 10%), 0 0 1px rgb(0 0 0 / 20%)`,
          marginTop: `162px`,
          width: `100%`,
          maxWidth: `1020px`,
          minHeight: dynamicHeightMapping,
          height: dynamicHeightMapping,
          marginLeft,
          zIndex: `1`,
        },
        [theme.breakpoints.down(`lg`)]: {
          marginTop: `0px`,
        },
        [theme.breakpoints.up(`lg`)]: {
          marginRight: "0.7%",
        },
      },

      contentGroup: {
        paddingRight: `2rem`,
        paddingTop: `22px`,
        paddingLeft: `122px`,
        "&.MuiBox-root-41 ": {
          overflow: `auto`,
          maxHeight: `42%`,
        },
        [theme.breakpoints.up(`lg`)]: {
          minHeight: `400px`,
          height: `400px`,
        },
        [theme.breakpoints.down(`xl`)]: {
          marginLeft: 0,
          paddingLeft: `2rem`,
          justifyContent: `unset`,
        },
        [theme.breakpoints.down(`sm`)]: {
          paddingRight: `0`,
          paddingLeft: `0`,
          paddingTop: `16px`,
          justifyContent: `start !important`,
          height: `auto`,
          minHeight: `auto`,
          "& a.Link": {
            justifyContent: `start !important`,
          },
        },
      },
      contentText: {
        width: `550px`,
        "& ul": {
          paddingLeft: `2rem`,
        },
        "& .MuiButton-label": {
          color: theme.palette.common.white,
        },
        [theme.breakpoints.down(`xl`)]: {
          paddingTop: `52px`,
          width: `100%`,
        },
        [theme.breakpoints.between(`md`, `md`)]: {
          paddingBottom: `26px`,
          paddingTop: `26px`,
        },
        [theme.breakpoints.down(`sm`)]: {
          padding: `16px`,
          width: "100%",
        },
      },
      setContentHeight: {
        [theme.breakpoints.down(`xl`)]: {
          minHeight: `330px`,
        },
      },
      heading: {
        marginBottom: "-8px !Important",
      },
    }
  },
)

export const isImageType = (o) => o === `Images` || o === `image`
export const isQuote = (o) => o === `yes` || o === true
export const updateTabIndex = () => {
  const links = Array.from(
    ssrWindow.document.querySelectorAll(".slick-slide a"),
  )
  forEach(links, (linkElement) => {
    const link = linkElement
    const parentSlide = link.closest(".slick-slide")
    const newTabIndex = parentSlide.classList.contains("slick-active")
      ? "0"
      : "-1"
    link.tabIndex = newTabIndex
  })
}

const StoryCarousel = ({
  storyCarouselList = [],
  backgroundColor = `grey`,
  componentId = ``,
  enableAutoplay = false,
  sliderSeconds = "",
  imageAltText = "",
  headingLevel = `h2`,
  videoThumbnailAltText = "",
  videoPlayButtonAltText = "",
}) => {
  const [openedVideoId, setOpenedVideoId] = useState(-1)
  // eslint-disable-next-line no-unused-vars
  const [isExpanded, setIsExpanded] = useState(true)
  // eslint-disable-next-line no-unused-vars
  const [isRedirect, setIsRedirect] = useState(false)
  const [isEnableAutoplayTrue, setIsEnableAutoplayTrue] = useState(
    !!enableAutoplay,
  )
  const [dynamicHeightMapping, setDynamicHeightMapping] = useState({})
  const [marginLeft, setMarginLeft] = useState("-21.5rem")
  const [activeSlide, setActiveSlide] = useState(0)

  const { classes } = useStyles({
    backgroundColor,
    isExpanded,
    dynamicHeightMapping,
    marginLeft,
  })
  const refs = map(storyCarouselList, () => createRef())

  const containerRef = createRef()

  /* 
    The SwipeableViews wrapper doesn't match the children component's height by default, 
    so we have to make a mapping of the child element heights and dynamically set the height when the activeStep is changed. 
  */
  useEffect(() => {
    if (
      !isEmpty(dynamicHeightMapping) &&
      every(refs, (ref) => !isNull(ref.current))
    ) {
      const newDynamicHeightMapping = map(
        refs,
        (ref) =>
          ref?.current?.firstChild?.children[1]?.firstChild?.offsetHeight,
      )
      setDynamicHeightMapping(Math.max(...newDynamicHeightMapping))
    }
  }, [refs])

  useEffect(() => {
    const el = containerRef.current?.querySelector(".slick-active")
    if (el) {
      const index = +el.dataset?.index + storyCarouselList.length
      containerRef.current
        ?.querySelector(`[data-index="${index}"] .Video`)
        ?.classList.add(`active`)
    }
  }, [])

  const handleZoom = () => {
    // Get the zoomLevel of the page
    const zoomLevel = getZoomLevel()

    // Check if the zoom level is greater than 4
    const isZoomLevelGreaterThanFour = zoomLevel > 4

    // If the zoom level is greater than 4
    if (isZoomLevelGreaterThanFour) {
      setMarginLeft("0")
    } else {
      setMarginLeft("-21.5rem")
    }
  }

  useEffect(() => {
    ssrWindow.addEventListener("resize", handleZoom)
    handleZoom()
    return () => {
      ssrWindow.removeEventListener("resize", handleZoom)
    }
  }, [])

  useEffect(() => {
    updateTabIndex()
    const carouselElement = ssrWindow.document.querySelector(".slick-slider")
    if (carouselElement) {
      carouselElement?.addEventListener("afterChange", updateTabIndex)
    }

    return () => {
      if (carouselElement) {
        carouselElement?.removeEventListener("afterChange", updateTabIndex)
      }
    }
  }, [activeSlide])

  const handleslider = () => {
    if (isEnableAutoplayTrue) {
      setTimeout(() => {
        sliderRef.slickPause()
      })
    } else {
      setTimeout(() => {
        sliderRef.slickPlay()
      })
    }

    setIsEnableAutoplayTrue(!isEnableAutoplayTrue)
  }

  const onNextClick = () => sliderRef.current.slickNext()
  const onPrevClick = () => sliderRef.current.slickPrev()

  const renderCarouselItem = (storyCarouselItem, index) => {
    const isImage = isImageType(storyCarouselItem.contentType)
    const isContentQuote = isQuote(storyCarouselItem.isQuote)
    return (
      <div
        key={`category-${storyCarouselItem.subHead}`}
        className={`StoryCarouselContent  ${classes.StoryCarouselContent} ${
          isImage ? `` : `video-thumbnail`
        } d-flex flex-md-row flex flex-lg-row align-items-center`}
      >
        <div
          className={`image-video ${classes.imageVideo} ${classes.imageVideoZoom} d-flex justify-content-center d-lg-block background-transparent`}
        >
          {isImage ? (
            <div
              alt={imageAltText || storyCarouselItem.imageAltText || ""}
              role="img"
              className="h-100 picture"
              style={{
                background: `url(${getContentUrl(storyCarouselItem.mediaSrc)})`,
                backgroundSize: `cover`,
                maxWidth: "628px",
              }}
            />
          ) : (
            <Video
              handleModal={(isOpen) => {
                if (isOpen) {
                  setOpenedVideoId(index)
                } else {
                  setOpenedVideoId(-1)
                }
              }}
              isExternalClick
              open={openedVideoId === index}
              videoThumbnailSrc={storyCarouselItem.videoThumbnailSrc}
              videoThumbnailAltText={
                videoThumbnailAltText ||
                storyCarouselItem.videoThumbnailAltText ||
                ""
              }
              videoPlayButtonAltText={
                videoPlayButtonAltText ||
                storyCarouselItem.videoPlayButtonAltText ||
                ""
              }
              mediaSrc={storyCarouselItem.mediaSrc}
              source={storyCarouselItem.source}
              altText={storyCarouselItem.videoThumbnailAltText}
              playIcon={storyCarouselItem.playIcon}
            />
          )}
        </div>
        <div className={`text-area bg-white ${classes.textArea}`}>
          <div className={`content-group  ${classes.contentGroup} d-flex`}>
            {storyCarouselList?.length > 0 && (
              <div
                className={`${classes.playpause}`}
                style={{ visibility: enableAutoplay ? "visible" : "hidden" }}
              >
                {isEnableAutoplayTrue ? (
                  <button
                    type="button"
                    onClick={handleslider}
                    aria-label={`pause ${storyCarouselItem.heading}`}
                  >
                    <ReactSVG
                      wrapper="div"
                      src={getContentUrl(
                        `/content/dam/wm/icons/controls/videos/40x40/pause-circle-outlined-green.svg`,
                      )}
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleslider}
                    aria-label={`play ${storyCarouselItem.heading}`}
                  >
                    <ReactSVG
                      wrapper="div"
                      src={getContentUrl(
                        `/content/dam/wm/icons/controls/videos/40x40/play-circle-outlined-green.svg`,
                      )}
                    />
                  </button>
                )}
              </div>
            )}
            <div
              className={`${classes.contentText} ${classes.setContentHeight}`}
            >
              {storyCarouselItem.eyebrowText ? (
                <Box py={1}>
                  <Typography
                    variant="subheading2"
                    className={`maax-font-weight-bold ${classes.eyebrowText}`}
                  >
                    {storyCarouselItem.eyebrowText}
                  </Typography>
                </Box>
              ) : null}
              {storyCarouselItem.heading ? (
                <Box pb={2}>
                  <Typography
                    variant={storyCarouselItem.headingLevel || headingLevel}
                    component={storyCarouselItem.headingLevel || headingLevel}
                    color="textSecondary"
                    className={` ${classes.heading}`}
                  >
                    {storyCarouselItem.heading}
                  </Typography>
                </Box>
              ) : null}
              <Box pb={isContentQuote ? 3 : 1}>
                <FormattedTextField
                  className={classes.bodyText}
                  variant={isContentQuote ? `h4` : `body1`}
                  component={isContentQuote ? `q` : `div`}
                >
                  {storyCarouselItem.description}
                </FormattedTextField>
              </Box>
              {storyCarouselItem.ctaButton &&
              storyCarouselItem.ctaType === "button" ? (
                <Box pt={1}>
                  <ButtonPrimary
                    analytics_key={
                      storyCarouselItem.analyticsKey ||
                      `${storyCarouselItem.subHead},${storyCarouselItem.ctaButton}`
                    }
                    className={`${classes.link} py-3`}
                    onClick={() =>
                      handleCTAClick(
                        storyCarouselItem.ctaButtonURL,
                        storyCarouselItem.isOpenLinkNewTab ? "_blank" : "_self",
                      )
                    }
                    href={storyCarouselItem.ctaButtonURL}
                    component="a"
                    children={storyCarouselItem.ctaButton}
                  />
                </Box>
              ) : (
                storyCarouselItem.ctaButton && (
                  <Box pt={1}>
                    <ButtonArrow
                      analytics_key={
                        storyCarouselItem.analyticsKey ||
                        `${storyCarouselItem.subHead},${storyCarouselItem.ctaButton}`
                      }
                      storyCarouselItem={storyCarouselItem}
                      className={`${classes.link} py-3`}
                      href={storyCarouselItem.ctaButtonURL}
                      target={
                        storyCarouselItem.isOpenLinkNewTab ? "_blank" : "_self"
                      }
                      isExternalLink={storyCarouselItem.isExternalLink}
                    >
                      {storyCarouselItem.ctaButton}
                    </ButtonArrow>
                  </Box>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleCTAClick = (path, isopentab) => {
    /*
      Prevent react-slick's "Go to slide on click" functionality if the user is clicking the ButtonArrow
      component to navigate to the desigated path. This prevents the carousel from shifting backwards by
      one slide while the redirect is ocurring.
     */
    setIsRedirect(true)
    ssrWindow.open(path, isopentab)
  }

  const renderArrow = (
    direction,
    onClick,
    isDesktop = false,
    backgroundColor,
  ) => {
    const arrowDirection = direction === `next` ? `right` : `left`
    const arrowColor = backgroundColor === "green1" ? "white" : "green"
    const buttonFillColor = backgroundColor === "green1" ? "white" : "green"
    const srcString = isDesktop
      ? getContentUrl(
          `/content/dam/wm/icons/controls/pagination/${buttonFillColor}-circle-shadow-arrow-${arrowDirection}.svg`,
        )
      : getContentUrl(
          `/content/dam/wm/icons/controls/pagination/arrow-${arrowDirection}-${arrowColor}.svg`,
        )
    return (
      <Button
        analytics_key={`ProductCarousel ${direction}`}
        ariaLabel={direction === `next` ? `next Slide` : `previous Slide`}
        className={`review-carousel-${direction}`}
        onClick={onClick}
      >
        <ReactSVG
          wrapper="div"
          className={`${classes.arrow} ${direction}`}
          src={srcString}
        />
      </Button>
    )
  }
  const isDesktop = ssrWindow.innerWidth > BOOTSTRAP_GRID_IPAD_PRO_MAX

  if (typeof storyCarouselList === `object` && storyCarouselList.length > 0) {
    return (
      <div
        className={`${classes.root}`}
        data-testid="storyCarousel"
        id={componentId}
      >
        <div className="container" ref={containerRef}>
          <Box className={`${classes.carousel}  pl-0 pr-0`}>
            <Slider
              afterChange={(current) => setActiveSlide(current)}
              adaptiveHeight
              autoplay={enableAutoplay}
              autoplaySpeed={Number(sliderSeconds) * 1000}
              dots
              appendDots={(dots) => (
                <ul aria-label="Carousel Slides">
                  {dots.map((item, index) => {
                    const dotsClassName = item?.props?.className
                    return (
                      <li className={dotsClassName} key={index}>
                        {React.cloneElement(item.props.children, {
                          "aria-pressed": dotsClassName === "slick-active",
                        })}
                      </li>
                    )
                  })}
                </ul>
              )}
              variableWidth={false}
              infinite
              initialSlide={0}
              slidesToShow={1}
              slidesToScroll={1}
              ref={(slider) => {
                sliderRef = slider
              }}
              nextArrow={renderArrow(
                `next`,
                onNextClick,
                isDesktop,
                backgroundColor,
              )}
              prevArrow={renderArrow(
                `prev`,
                onPrevClick,
                isDesktop,
                backgroundColor,
              )}
            >
              {map(storyCarouselList, (item, i) => (
                <div
                  ref={refs[i]}
                  key={i}
                  style={{}}
                  className={`${classes.setHeight}`}
                >
                  {renderCarouselItem(storyCarouselList[i], i)}
                </div>
              ))}
            </Slider>
          </Box>
        </div>
      </div>
    )
  }
  return null
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// StoryCarousel.propTypes = {
//   storyCarouselList: PropTypes.arrayOf(
//     PropTypes.shape({
//       /** Image - Square image that sits on text card, option to include static image or video */
//       mediaSrc: PropTypes.string,
//       /** Alt Text for Image */
//       imageAltText: PropTypes.string,
//       /**
//         Video ID or URL? Optional, if video is included (will replace square image)
//         Should utilize Wistia player
//         Options: `image`, `video` or sample JSON showed: `Images`, `Videos`
//       */
//       contentType: PropTypes.string,
//       /**
//         Video Thumbnail
//         950x534 Required if video is included (will replace square image)
//       */
//       videoThumbnailSrc: PropTypes.string,
//       /** altText for thumbnail Image */
//       videoThumbnailAltText: PropTypes.string,
//       /** Product Info - Subhead - 35 char max */
//       subHead: PropTypes.string,
//       /**
//         Product Info - Description
//         Option to feature a pull quote or more standard product-type content with headline/copy/cta
//       */
//       description: PropTypes.string,
//       /**
//         CTA Button - User can be directed to product detail page on click/tap
//       */
//       ctaButton: PropTypes.string,
//       /**
//         CTA Button - src
//       */
//       ctaButtonUrl: PropTypes.string,
//       /**
//         Pull quote - True if the description field is a pull quote, false otherwise.
//         Options: `no`, `yes`
//       */
//       /**
//         Pull quote - Person's Name - default to green-2, body-left in bold
//       */
//       /** Analytics key */
//       analyticsKey: PropTypes.string,
//       personName: PropTypes.string,
//       /** Pull quote - Person's Title */
//       personTitle: PropTypes.string,
//       /** Pull quote - Person's Location */
//       personLocation: PropTypes.string,
//       /** Eye Brow Text */
//       eyebrowText: PropTypes.string,
//     }),
//   ),
//   /**
//     Background Color
//     Options: `grey`, `white`
//   */
//   backgroundColor: PropTypes.string,
//   componentId: PropTypes.string,
//   enableAutoplay: PropTypes.oneOfType([PropTypes.bool]),
//   sliderSeconds: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// StoryCarousel.defaultProps = {
//   backgroundColor: `grey`,
//   componentId: ``,
//   storyCarouselList: ``,
//   enableAutoplay: false,
//   sliderSeconds: "",
// }

export default StoryCarousel
